import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as authApi from '@/features/auth/api';

/**
 * `POST /auth/forgot-password` (Phase G-7, Job 7). The backend returns the
 * identical envelope whether or not the email is registered (ADR-018,
 * Decision 11), so this page shows exactly one neutral outcome after
 * submission - never a different message for "email exists" vs. "email
 * doesn't exist", which would reintroduce the enumeration signal the
 * backend deliberately avoids.
 *
 * There is no email/SMTP delivery yet (Phase G-7, OD-4) - the backend
 * issues a real token but has no way to send it to the user. This page
 * still completes the request/response contract correctly; delivery is a
 * documented, separate, post-G-7 dependency.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);

    authApi
      .forgotPassword({ email })
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        setError('Terjadi kesalahan. Silakan coba lagi.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <h1 className="sr-only">Lupa Kata Sandi</h1>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Lupa Kata Sandi</CardTitle>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="flex flex-col gap-4">
              <Alert>
                <AlertDescription>
                  Jika email tersebut terdaftar, tautan pengaturan ulang kata sandi telah dikirim.
                </AlertDescription>
              </Alert>
              <Button asChild variant="outline">
                <Link to="/login">Kembali ke Halaman Masuk</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                Masukkan email akun Anda. Kami akan mengirimkan tautan untuk mengatur ulang kata
                sandi jika email tersebut terdaftar.
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor="forgot-password-email">Email</Label>
                <Input
                  id="forgot-password-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  required
                />
              </div>
              {error !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Mengirim...' : 'Kirim Tautan Reset'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary underline-offset-4 hover:underline">
                  Kembali ke Halaman Masuk
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
